import React, { useEffect, useState } from 'react';
import { Plus, X } from 'lucide-react';
import {
  getQuizzesForCourse,
  getAssignmentsForCourse,
  createQuizAdmin,
  deleteQuizAdmin,
  addQuestionAdmin,
  deleteQuestionAdmin,
  createAssignmentAdmin,
  deleteAssignmentAdmin,
} from '../../lib/assess';
import type { Quiz, Assignment } from '../../types/assess';
import { EmptyState, Panel, PanelHead, Stamp, fieldCls, labelCls } from '../../components/lms/ui';

const AssessEditor: React.FC<{ courseId: string }> = ({ courseId }) => {
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [quizTitle, setQuizTitle] = useState('');
  const [assignTitle, setAssignTitle] = useState('');
  const [qForm, setQForm] = useState<Record<string, { q: string; opts: string; correct: number }>>({});
  const [error, setError] = useState('');

  const load = async () => {
    setQuizzes(await getQuizzesForCourse(courseId).catch(() => []));
    setAssignments(await getAssignmentsForCourse(courseId).catch(() => []));
  };

  // Reload when the course changes; load is intentionally scoped to that course.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(); }, [courseId]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-rule pb-3">
        <div>
          <h2 className="text-[17px] font-semibold tracking-[-0.01em] text-ink">Assessment desk</h2>
          <p className="mt-1 text-[12.5px] text-ink-2">Quizzes grade immediately; assignments arrive in the grading queue for a mark and feedback.</p>
        </div>
        <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-ink-2">
          {quizzes.length} {quizzes.length === 1 ? 'quiz' : 'quizzes'} · {assignments.length} {assignments.length === 1 ? 'assignment' : 'assignments'}
        </span>
      </div>

      {error && (
        <p className="border border-danger/25 bg-danger-soft px-3 py-2 text-[12px] text-danger" role="alert">{error}</p>
      )}

      <div className="grid gap-6 xl:grid-cols-2 xl:items-start">
        <Panel>
          <PanelHead
            title="Quizzes"
            action={<span className="font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">{quizzes.length} on file</span>}
          />
          <form
            className="flex flex-col gap-2 border-b border-rule px-5 py-4 sm:flex-row sm:items-end"
            onSubmit={async (e) => {
              e.preventDefault();
              if (!quizTitle.trim()) return;
              setError('');
              try {
                await createQuizAdmin(courseId, quizTitle.trim());
                setQuizTitle('');
                await load();
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Could not add the quiz.');
              }
            }}
          >
            <div className="min-w-0 flex-1">
              <label htmlFor="new-quiz" className={labelCls}>New quiz</label>
              <input id="new-quiz" value={quizTitle} onChange={(e) => setQuizTitle(e.target.value)} placeholder="Quiz title" className={fieldCls} />
            </div>
            <button type="submit" className="lms-btn-secondary !px-3.5 !py-2 !text-[12px]">
              <Plus className="h-3.5 w-3.5" strokeWidth={1.5} /> Add quiz
            </button>
          </form>

          {quizzes.length === 0 ? (
            <EmptyState title="No quizzes set" body="Add a quiz above, then write its questions. A quiz grades itself once a student submits it." />
          ) : (
            <div className="divide-y divide-rule">
              {quizzes.map((q) => (
                <section key={q.id} className="px-5 py-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-[13.5px] font-semibold text-ink">{q.title}</p>
                      <p className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">
                        Pass mark {q.pass_mark}% · {(q.questions ?? []).length} questions
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={async () => {
                        await deleteQuizAdmin(q.id);
                        await load();
                      }}
                      className="lms-btn-quiet !px-2 !py-1.5 !text-[11px] hover:!text-danger"
                    >
                      Delete quiz
                    </button>
                  </div>

                  {(q.questions ?? []).length > 0 && (
                    <ol className="mt-3 divide-y divide-rule/70 border-y border-rule/70">
                      {(q.questions ?? []).map((qq, i) => (
                        <li key={qq.id} className="flex items-start gap-3 py-2">
                          <span className="mt-0.5 font-mono text-[9px] text-ink-2">{String(i + 1).padStart(2, '0')}</span>
                          <div className="min-w-0 flex-1">
                            <p className="text-[12px] leading-5 text-ink">{qq.question}</p>
                            <p className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.08em] text-ink-2">
                              Answer: {qq.options[qq.correct_index]}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={async () => {
                              await deleteQuestionAdmin(qq.id);
                              await load();
                            }}
                            className="flex h-5 w-5 flex-none items-center justify-center text-ink-2 transition-colors hover:bg-danger-soft hover:text-danger"
                            aria-label={`Delete question ${i + 1}`}
                          >
                            <X className="h-3 w-3" strokeWidth={1.5} />
                          </button>
                        </li>
                      ))}
                    </ol>
                  )}

                  <form
                    className="mt-3 space-y-2.5"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const f = qForm[q.id];
                      if (!f?.q.trim() || !f.opts.trim()) return;
                      setError('');
                      const opts = f.opts.split(',').map((s) => s.trim()).filter(Boolean);
                      try {
                        await addQuestionAdmin(q.id, f.q.trim(), opts, Math.min(f.correct, opts.length - 1));
                        setQForm({ ...qForm, [q.id]: { q: '', opts: '', correct: 0 } });
                        await load();
                      } catch (err) {
                        setError(err instanceof Error ? err.message : 'Could not add the question.');
                      }
                    }}
                  >
                    <div>
                      <label htmlFor={`q-${q.id}`} className={labelCls}>Question</label>
                      <input
                        id={`q-${q.id}`}
                        value={qForm[q.id]?.q || ''}
                        onChange={(e) => setQForm({ ...qForm, [q.id]: { q: e.target.value, opts: qForm[q.id]?.opts || '', correct: qForm[q.id]?.correct || 0 } })}
                        placeholder="What does NXP stand for?"
                        className={fieldCls}
                      />
                    </div>
                    <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_110px_auto] sm:items-end">
                      <div>
                        <label htmlFor={`o-${q.id}`} className={labelCls}>Options, comma-separated</label>
                        <input
                          id={`o-${q.id}`}
                          value={qForm[q.id]?.opts || ''}
                          onChange={(e) => setQForm({ ...qForm, [q.id]: { q: qForm[q.id]?.q || '', opts: e.target.value, correct: qForm[q.id]?.correct || 0 } })}
                          placeholder="NXP, BL, LC, COO"
                          className={fieldCls}
                        />
                      </div>
                      <div>
                        <label htmlFor={`k-${q.id}`} className={labelCls}>Correct #</label>
                        <input
                          id={`k-${q.id}`}
                          type="number"
                          min={0}
                          value={qForm[q.id]?.correct || 0}
                          onChange={(e) => setQForm({ ...qForm, [q.id]: { q: qForm[q.id]?.q || '', opts: qForm[q.id]?.opts || '', correct: Number(e.target.value) } })}
                          className={`${fieldCls} tnum`}
                        />
                      </div>
                      <button type="submit" className="lms-btn-secondary !px-3 !py-2 !text-[11.5px]">
                        Add question
                      </button>
                    </div>
                  </form>
                </section>
              ))}
            </div>
          )}
        </Panel>

        <Panel>
          <PanelHead
            title="Assignments"
            action={<span className="font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">{assignments.length} on file</span>}
          />
          <form
            className="flex flex-col gap-2 border-b border-rule px-5 py-4 sm:flex-row sm:items-end"
            onSubmit={async (e) => {
              e.preventDefault();
              if (!assignTitle.trim()) return;
              setError('');
              try {
                await createAssignmentAdmin(courseId, assignTitle.trim());
                setAssignTitle('');
                await load();
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Could not add the assignment.');
              }
            }}
          >
            <div className="min-w-0 flex-1">
              <label htmlFor="new-assignment" className={labelCls}>New assignment</label>
              <input id="new-assignment" value={assignTitle} onChange={(e) => setAssignTitle(e.target.value)} placeholder="Assignment title" className={fieldCls} />
            </div>
            <button type="submit" className="lms-btn-secondary !px-3.5 !py-2 !text-[12px]">
              <Plus className="h-3.5 w-3.5" strokeWidth={1.5} /> Add assignment
            </button>
          </form>

          {assignments.length === 0 ? (
            <EmptyState title="No assignments set" body="Add an assignment above. Student submissions land in the grading tab for a mark and written feedback." />
          ) : (
            <ul className="divide-y divide-rule">
              {assignments.map((a, ai) => (
                <li key={a.id} className="flex items-center gap-3 px-5 py-3.5">
                  <span className="font-mono text-[9px] text-ink-2">{String(ai + 1).padStart(2, '0')}</span>
                  <p className="min-w-0 flex-1 truncate text-[12.5px] font-medium text-ink">{a.title}</p>
                  <Stamp tone="neutral">Brief</Stamp>
                  <button
                    type="button"
                    onClick={async () => {
                      await deleteAssignmentAdmin(a.id);
                      await load();
                    }}
                    className="lms-btn-quiet !px-2 !py-1.5 !text-[11px] hover:!text-danger"
                  >
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
};

export default AssessEditor;
